import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { Button } from "react-aria-components";

import { cn } from "@/utils/cn";

import { EditButton } from "./EditButton";

type CollapsibleCardProps = {
  children: React.ReactNode;
  defaultOpen?: boolean;
  icon: React.ReactNode;
  /** Shows an edit button in the header; editing also expands the card. */
  onEdit?: () => void;
  title: string;
};

export function CollapsibleCard({
  children,
  defaultOpen = true,
  icon,
  onEdit,
  title,
}: CollapsibleCardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <section className="rounded-xl border border-border-primary bg-bg-primary">
      <header className="flex items-center gap-2.5 px-5 py-4">
        <span className="flex size-6 shrink-0 items-center justify-center text-fg-brand-primary">
          {icon}
        </span>
        <h3 className="flex-1 text-lg font-semibold text-text-primary">
          {title}
        </h3>
        {onEdit && (
          <EditButton
            label={title}
            onPress={() => {
              setIsOpen(true);
              onEdit();
            }}
          />
        )}
        <Button
          aria-expanded={isOpen}
          aria-label={isOpen ? `Collapse ${title}` : `Expand ${title}`}
          className="cursor-pointer rounded-md p-1 text-fg-quaternary outline-hidden transition-colors hover:bg-bg-primary-hover hover:text-text-primary"
          onPress={() => setIsOpen((v) => !v)}
        >
          <ChevronDown
            className={cn(
              "size-5 transition-transform duration-300",
              isOpen && "rotate-180",
            )}
          />
        </Button>
      </header>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            animate={{ height: "auto", opacity: 1 }}
            className="overflow-hidden"
            exit={{ height: 0, opacity: 0 }}
            initial={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className="px-5 pb-5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
